<template>
  <div id="summary-tab" class="px-5">
    <!-- <div v-if="hasTeacherPermissionsInCourse" class="flex my-6">
      <SummaryTabs
        :tabs="summary_tabs"
        @role="(tab) => (selectedOption = tab)"
      />
    </div> -->
    <div
      v-if="selectedOption === 'tab_class_wide'"
      :class="!hasTeacherPermissionsInCourse ? 'mt-7' : ''"
      class="grid grid-cols-1 lg:grid-cols-2 gap-10 my-8"
    >
      <div id="responses-chart">
        <div class="flex justify-between items-center">
          <h5 class="title mb-1">Responses</h5>
          <div class="flex items-center">
            <button class="mr-2 ml-4" v-popover:percentages-info.left>
              <i class="fas fa-info-circle"></i>
            </button>
            <button class="mr-2" v-popover:percentages-questions.left>
              <i class="fas fa-question-circle"></i>
            </button>
          </div>
        </div>
        <p class="about mb-4">
          Participation data about student responses and comments in the
          discussion.
        </p>
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-auto"
          name="percentages-info"
          :width="600"
        >
          <div class="px-6 py-4">
            <h5 class="font-semibold">Information</h5>
          </div>
          <div class="border-b"></div>
          <div class="px-6 py-4">
            <p>
              These statistics are helpful to gather a birds-eye view of student
              engagement.
            </p>
          </div>
        </popover>
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-20"
          name="percentages-questions"
          :width="600"
        >
          <div class="px-6 py-4">
            <h5 class="font-semibold">Reflection Questions</h5>
          </div>
          <div class="border-b"></div>
          <ul class="list-decimal list-inside px-6 py-4">
            <li class="mb-2">
              What do these percentages tell us about our discussion?
            </li>
            <li class="mb-2">
              Is it important for everyone to comment? Why or why not?
            </li>
            <li class="mb-2">
              Is it important for everyone to receive feedback? Why or why not?
            </li>
            <li>
              How might we improve these numbers in future Written Roundtables?
            </li>
          </ul>
        </popover>
        <div v-if="isLoadingResponses === false">
          <ProgressiveBar
            class="mb-4"
            :percentage="Math.round(participationRate())"
            :textAbout="'submitted a response'"
            :height="12"
          />
          <ProgressiveBar
            class="mb-4"
            :percentage="
              Math.round(summaryDetails.studentsWhoMadeAtLeastOneComment)
            "
            :textAbout="'gave at least one comment'"
            :height="12"
          />
          <ProgressiveBar
            class="mb-4"
            :percentage="
              Math.round(summaryDetails.studentsWhoReceivedAtLeastOneComment)
            "
            :textAbout="'received at least one comment'"
            :height="12"
          />
        </div>
        <div v-else>
          <div class="animate-pulse">
            <div class="mb-2">
              <div class="w-1/5 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div class="mb-2">
              <div class="w-1/12 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div class="mb-2">
              <div class="w-1/6 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      </div>
      <div id="average-comment">
        <div class="flex justify-between items-center">
          <h5 class="title mb-1">Average Comment Length</h5>
          <div class="flex items-center">
            <button class="mr-2 ml-4" v-popover:comment-length-info.left>
              <i class="fas fa-info-circle"></i>
            </button>
            <button class="mr-2" v-popover:comment-length-questions.left>
              <i class="fas fa-question-circle"></i>
            </button>
          </div>
        </div>
        <p class="about mb-4">
          The average length (in words) of each student comment in the
          RoundTable.
        </p>
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-auto"
          name="comment-length-info"
          :width="600"
        >
          <div class="px-6 py-4">
            <h5 class="font-semibold">Information</h5>
          </div>
          <div class="border-b"></div>
          <div class="px-6 py-4">
            <p>
              A typical sentence is about 15 words long. Our hope is that
              students provide at least two sentences when giving feedback to
              their peers.
            </p>
          </div>
        </popover>
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-20"
          name="comment-length-questions"
          :width="600"
        >
          <div class="px-6 py-4">
            <h5 class="font-semibold">Reflection Questions</h5>
          </div>
          <div class="border-b"></div>
          <ul class="list-decimal list-inside px-6 py-4">
            <li class="mb-2">
              Why does the average word length of our peer comments/feedback
              matter?
            </li>
            <li class="mb-2">
              Does the number of words in a comment reflect the quality of the
              comment?
            </li>
            <li>How can we improve in our next Written RoundTable?</li>
          </ul>
        </popover>
        <div v-if="isLoadingResponses === false">
          <AverageProgress
            :totalWords="Math.round(summaryDetails.averageCommentLength)"
            class="mt-10"
          />
        </div>
        <div v-else>
          <div class="animate-pulse">
            <div class="mb-2">
              <div class="w-1/5 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div class="mb-2">
              <div class="w-1/12 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div class="mb-2">
              <div class="w-1/6 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      </div>
      <div id="keywordCloud">
        <div class="flex justify-between items-center">
          <div class="flex items-center">
            <h5 class="title">Keyword Cloud</h5>
            <button class="ml-3 text-lg text-pinky flex items-center">
              <i class="fa-solid fa-language"></i>
              <span class="text-sm font-semibold ml-2">{{
                cloudWordLanguage
              }}</span>
            </button>
          </div>
          <div class="flex items-center">
            <button
              v-if="hasTeacherPermissionsInCourse"
              @click="isEditWordCloudOpen = true"
              class="px-4 py-2 ml-3 text-xs font-semibold text-gray-600 bg-coolGray-100 hover:bg-coolGray-200 rounded"
            >
              <i class="fa-solid fa-pen-to-square mr-1.5"></i>Edit Words
            </button>
            <button class="mr-2 ml-4" v-popover:word-cloud-info>
              <i class="fas fa-info-circle"></i>
            </button>
            <button class="mr-2" v-popover:word-cloud-questions>
              <i class="fas fa-question-circle"></i>
            </button>
          </div>
        </div>
        <p class="about mb-4">
          A keyword cloud for the most recurring words across all student
          responses.
        </p>
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-auto"
          name="word-cloud-info"
          :width="600"
        >
          <div class="px-6 py-4">
            <h5 class="font-semibold">Information</h5>
          </div>
          <div class="border-b"></div>
          <div class="px-6 py-4">
            <h5 class="font-semibold text-gray-900 mb-1">
              The Idea Cloud analyzes the RoundTable for frequent words across
              student submissions.
            </h5>
            <p class="font-normal text-gray-500 mb-3">
              The bigger the word in the cloud, the more times it was used!
            </p>
            <p class="font-normal text-gray-900 mb-3">
              Share this visual and become a
              <span @click="toBlogPost()" class="hover:underline cursor-pointer"
                >Parlay Trailblazer!</span
              >
            </p>
            <div v-if="hasTeacherPermissionsInCourse" class="flex items-center">
              <button
                @click="shareOnFB()"
                class="bg-blue-500 text-white px-4 py-1 rounded mr-3"
              >
                Share <i class="ml-1 fa-brands fa-facebook"></i>
              </button>
              <button
                @click="shareOnTW()"
                class="bg-lightBlue-300 text-white px-4 py-1 rounded"
              >
                Tweet <i class="fa-brands fa-twitter"></i>
              </button>
            </div>
          </div>
        </popover>
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-20"
          name="word-cloud-questions"
          :width="600"
        >
          <div class="px-6 py-4">
            <h5 class="font-semibold">Reflection Questions</h5>
          </div>
          <div class="border-b"></div>
          <ul class="list-decimal list-inside px-6 py-4">
            <li class="mb-2">
              What words stood out? Did any surprise you? Why or why not?
            </li>
            <li class="mb-2">
              Were there any words you thought would be more prominent but were
              not?
            </li>
            <li>What themes or perspectives stick out in this word cloud?</li>
          </ul>
        </popover>
        <div v-if="isLoadingResponses === false">
          <WordCloud
            @cloudLanguague="(language) => (cloudWordLanguage = language)"
          />
        </div>
        <div v-else>
          <div class="animate-pulse">
            <div class="mb-2">
              <div class="w-1/5 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div class="mb-2">
              <div class="w-1/12 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div class="mb-2">
              <div class="w-1/6 h-3 bg-gray-200 dark:bg-gray-700 mb-2"></div>
              <div class="w-full h-3 bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      </div>
      <div id="commentChord">
        <div class="flex justify-between items-center">
          <h5 class="title mb-1">Comment Chord</h5>
          <div class="flex items-center">
            <button class="mr-2 ml-4" v-popover:comment-chord-info.left>
              <i class="fas fa-info-circle"></i>
            </button>
            <button class="mr-2" v-popover:comment-chord-questions.left>
              <i class="fas fa-question-circle"></i>
            </button>
            <popover
              class="mr-10 p-0 bg-white drop-shadow absolute"
              name="comment-chord-info"
              :width="600"
              style="z-index: 10"
            >
              <div class="px-6 py-4">
                <h5 class="font-semibold">Information</h5>
              </div>
              <div class="border-b"></div>
              <div class="px-6 py-4">
                <h5 class="font-semibold text-gray-900 mb-1">
                  The Comment Chord shows how many comments and replies each
                  student submitted.
                </h5>
                <p class="font-normal text-gray-500 mb-3">
                  Hover over to show the relationship between two students!
                </p>
                <p class="font-normal text-gray-900 mb-3">
                  Share this visual and become a
                  <span
                    @click="toBlogPost()"
                    class="hover:underline cursor-pointer"
                    >Parlay Trailblazer!</span
                  >
                </p>
                <div
                  v-if="hasTeacherPermissionsInCourse"
                  class="flex items-center mb-4"
                >
                  <button
                    @click="shareOnFB()"
                    class="bg-blue-500 text-white px-4 py-1 rounded mr-3"
                  >
                    Share <i class="ml-1 fa-brands fa-facebook"></i>
                  </button>
                  <button
                    @click="shareOnTW()"
                    class="bg-lightBlue-300 text-white px-4 py-1 rounded"
                  >
                    Tweet <i class="fa-brands fa-twitter"></i>
                  </button>
                </div>
                <p class="text-gray-500 text-xs">
                  Students' names will not be visible in the public version of
                  the graph*
                </p>
              </div>
            </popover>
            <popover
              class="mr-10 p-0 bg-white drop-shadow absolute z-20"
              name="comment-chord-questions"
              :width="650"
            >
              <div class="px-6 py-4">
                <h5 class="font-semibold">Reflection Questions</h5>
              </div>
              <div class="border-b"></div>
              <ul class="list-decimal list-inside px-6 py-4">
                <li class="mb-2">
                  Was this a balanced conversation or did a few voices dominate?
                </li>
                <li class="mb-2">
                  Were there many students who didn't provide any comments or
                  replies?
                </li>
                <li>
                  Overall, did we respond to many comments or replies to develop
                  the conversation or was there a lot of one way communication?
                </li>
              </ul>
            </popover>
          </div>
        </div>
        <p class="about mb-4">
          A summary of the comments and replies between students.
        </p>
        <CommentChord />
      </div>
    </div>
    <ExportEngagementDataModal
      v-if="isExportModalOpen === true"
      :fileName="exportableFileName"
      :getFile="fetchClassCSVdata"
      @close="isExportModalOpen = false"
      :showButtons="Boolean(getCurrentRT.submissions.length)"
    />
    <EditWordCloud
      v-if="isEditWordCloudOpen"
      @close="isEditWordCloudOpen = false"
    />
  </div>
</template>
<script>
  import Vue from 'vue';
  import Popover from 'vue-js-popover';
  import moment from 'moment';
  import { mapGetters } from 'vuex';
  import ProgressiveBar from '../components/summaryComponents/ProgressiveBar.vue';
  import AverageProgress from '../components/summaryComponents/AverageComment.vue';
  import StudentsTable from '../components/summaryComponents/StudentsTable.vue';
  import WordCloud from '../components/summaryComponents/KeyWordCloud.vue';
  import CommentChord from '../components/summaryComponents/CommentChord.vue';
  import roundtableServices from 'features/onlineRoundtables/onlineRoundtables.services.js';
  import getClassSummaryService from '../services/grades/classSummary';
  import ExportEngagementDataModal from 'components/modals/exportEngagementDataModal';
  import Popper from 'vue-popperjs';
  import URLPath from 'services/apiConfigs';

  import 'vue-popperjs/dist/vue-popper.css';
  import allowSharing from 'features/onlineRoundtables/services/dataVisualization/allowSharing';
  import onlineRoundtableSockets from 'mixins/sockets/onlineRoundtableSocketEvents';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';
  Vue.use(Popover);

  export default Vue.extend({
    components: {
      ProgressiveBar,
      AverageProgress,
      StudentsTable,
      ExportEngagementDataModal,
      WordCloud,
      CommentChord,
      Popper,
      EditWordCloud: () =>
        import('../components/summaryComponents/EditWordCloud.vue'),
      SummaryTabs: () =>
        import('../../../features/dashboard/TabsRoleSelect.vue'),
    },
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getOrderedSubmissions',
        'hasTeacherPermissionsInCourse',
        'getProfile',
      ]),
      exportableFileName() {
        return `${this.getCurrentRT.title} - Written - Export ${moment().format(
          'L'
        )}.csv`;
      },
      sharing_url() {
        const roundtableID = this.$route.params.roundtableID;
        return `${URLPath.apiURL}/api/roundtables/${roundtableID}/idea-cloud-public`;
      },
    },
    mixins: [onlineRoundtableSockets, courseSocketMixin],
    data() {
      return {
        selectedOption: 'tab_class_wide' /* Default Option */,
        isLoadingResponses: Boolean,
        isExportModalOpen: false,
        isOpenSelect: false,
        cloudWordLanguage: '',
        isEditWordCloudOpen: false,
        summaryDetails: {
          studentsWhoMadeAtLeastOneComment: Number,
          studentsWhoReceivedAtLeastOneComment: Number,
          averageCommentLength: Number,
          averageCommentsPerStudent: Number,
        },
        summary_tabs: [
          {
            id: 'tab_class_wide',
            name: 'Class-Wide',
            icon: 'fa-solid fa-user-group',
            show: true,
            is_selected: true,
          },
          {
            id: 'tab_individual_summary',
            name: 'Individual',
            icon: 'fa-solid fa-user',
            show: true,
            is_selected: false,
          },
        ],
      };
    },
    methods: {
      selectOneOption: function (valueOption) {
        this.selectOption = valueOption;
      },
      openSelect: function () {
        this.isOpenSelect = !this.isOpenSelect;
      },
      handleSetOption: function (value) {
        this.selectedOption = value;
        this.isOpenSelect = false;
      },
      setAllowingToTrue() {
        if (this.is_sharing_allowed) {
          // Return empty promise to allow a .then() in all scenarios
          return Promise.resolve(null);
        }
        const rt_id = this.getCurrentRT.id;
        const body = { idea_cloud: true };
        const sharing_settings = Object.assign(
          {},
          this.getCurrentRT.sharing_settings,
          body
        );
        this.$store.dispatch('updateRTDetails', { sharing_settings });
        return allowSharing(rt_id, body);
      },

      formatParam(param) {
        return param
          .replace(/%/g, '%25')
          .replace(/ /g, '%20')
          .replace(/\$/g, '%24')
          .replace(/#/g, '%23');
      },
      toBlogPost: function () {
        window.open(
          'https://parlayideas.com/revolutionize-discussion-become-a-parlay-trailblazer/',
          '_blank'
        );
      },
      shareOnFB() {
        // this.setAllowingToTrue().then(() => {
        //   // eslint-disable-next-line no-undef
        //   FB.ui({
        //     method: 'share',
        //     href: this.formatParam(this.sharing_url),
        //   });
        // });
      },
      shareOnTW() {
        this.setAllowingToTrue().then(() => {
          const rtTitle = this.getCurrentRT.title;
          const url = this.formatParam(this.sharing_url);
          const text = this.formatParam(
            `Check out this Idea Cloud from our class discussion "${rtTitle}" on @parlayideas`
          );
          window.open(
            `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
            '',
            'scrollbars=yes,width=650,height=280'
          );
        });
      },
      numberStartedSubmissions() {
        return this.getOrderedSubmissions.filter(
          (submission) =>
            submission.is_started &&
            submission.start_date !== null &&
            submission.prompt_response !== null &&
            !submission.teacher_submission
        ).length;
      },
      participationRate() {
        const rate =
          (this.numberStartedSubmissions() * 100) /
          this.getOrderedSubmissions.filter((s) => !s.teacher_submission)
            .length;
        return Math.round(+rate || 0);
      },
      fetchClassCSVdata() {
        return roundtableServices
          .generateExportableData(this.getCurrentRT.id)
          .then((res) => res.data);
      },
      showModal() {
        this.isExportModalOpen = true;
      },
      closeModal() {
        this.isExportModalOpen = false;
      },
    },
    mounted() {
      const { courseID, roundtableID } = this.$route.params;
      this.setupCourseSocket(courseID);
      this.leaveRoundtableSocket(roundtableID);
      this.setupRoundtableSocket(roundtableID);
      getClassSummaryService(this.getCurrentRT.id).then((res) => {
        this.isLoadingResponses = true;
        this.summaryDetails = {
          studentsWhoMadeAtLeastOneComment:
            res.data.students_who_made_at_least_one_comment,
          averageCommentLength: res.data.average_comment_length,
          averageCommentsPerStudent: res.data.average_comments_per_student,
          studentsWhoReceivedAtLeastOneComment:
            res.data.students_who_received_at_least_one_comment,
        };
        this.isLoadingResponses = false;
      });
      // window.fbAsyncInit = function () {
      //   // eslint-disable-next-line no-undef
      //   FB.init({
      //     appId: '1550372328564661',
      //     autoLogAppEvents: true,
      //     xfbml: true,
      //     version: 'v2.12',
      //   });
      // };
      // (function (d, s, id) {
      //   let js,
      //     fjs = d.getElementsByTagName(s)[0];
      //   if (d.getElementById(id)) {
      //     return;
      //   }
      //   js = d.createElement(s);
      //   js.id = id;
      //   js.src = 'https://connect.facebook.net/en_US/sdk.js';
      //   fjs.parentNode.insertBefore(js, fjs);
      // })(document, 'script', 'facebook-jssdk');

      if (this.getProfile.role !== 'teacher') {
        this.showEditKeywordsButton = false;
      }
    },
    beforeDestroy() {
      this.leaveRoundtableSocket(this.$route.params.roundtableID);
    },
  });
</script>
<style>
  .title {
    font-size: 20px;
    font-weight: 600;
    color: #283248;
  }
  .about {
    color: #7f89a1;
    font-size: 14px;
  }

  .select-wrapper {
    position: relative;
  }

  .select-wrapper::after {
    content: '';
    font-size: 1rem;
    top: 6px;
    right: 10px;
    position: absolute;
  }
</style>



// WEBPACK FOOTER //
// summaryTab.vue?0272f1fd